import axios, { InternalAxiosRequestConfig, AxiosError } from "axios";
import { toast } from "react-hot-toast";
// import { toast } from "react-hot-toast";

const handleSuccess = (response: any) => {
  switch (response.status) {
    case 200:
      // Handle success logic here
      break;
    case 201:
      // Handle success logic for status code 201 here
      break;
    default:
      // Handle unknown error logic here
      break;
  }
};

const handleClientError = (response: any) => {
  if (!response) {
    toast.error("Client request failed. Please check your network connection and try again.");
    return;
  }

  switch (response.status) {
    case 400:
      toast.error(response.data?.message || "Bad request. Please check your input.");
      break;
    case 401:
      if (!response.config?.url?.includes("auth/login")) {
        break;
      }
      toast.error(response.data?.message || "Invalid credentials. Please try again.");
      break;
    default:
      toast.error(response.data?.message || "An error occurred. Please try again.");
      break;
  }
};

const getRange = (code: number) => {
  if (code >= 200 && code < 300) {
    return "SUCCESS_RANGE";
  }
  if (code >= 400 && code < 500) {
    return "CLIENT_ERROR_RANGE";
  }
  if (code >= 500 && code < 600) {
    return "SERVER_ERROR_RANGE";
  }
  return "UNKNOWN";
};

const handleResponse = (response: any) => {
  if (!response) {
    toast.error("Request failed. Please check your network connection and try again.");
    return;
  }

  const range = getRange(response.status);
  switch (range) {
    case "SUCCESS_RANGE":
      handleSuccess(response);
      break;
    case "CLIENT_ERROR_RANGE":
      handleClientError(response);
      break;
    case "SERVER_ERROR_RANGE":
      toast.error(response.data?.message || "Server error occurred. Please try again later.");
      break;
    case "UNKNOWN":
      toast.error(response.data?.message || "An unexpected error occurred.");
      break;
    default:
      toast.error(response.data?.message || "An unexpected error occurred.");
      break;
  }
};

const axiosInstance: any = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  // Other Axios configurations...
});

const getStoredCompanyId = (): string | null => {
  const storedCompany = localStorage.getItem("company");
  if (!storedCompany) {
    return null;
  }

  try {
    const parsedCompany = JSON.parse(storedCompany);
    return parsedCompany?.id ? String(parsedCompany.id) : null;
  } catch {
    return null;
  }
};

const getStoredAccessToken = (): string | null => {
  let token = localStorage.getItem("access-token");
  if (!token) {
    token = localStorage.getItem("accessToken") || localStorage.getItem("token");
  }

  if (!token) {
    return null;
  }

  return token || null;
};

const getStoredRefreshToken = (): string | null => {
  const token = localStorage.getItem("refresh-token");
  if (!token) {
    return null;
  }

  return token;
};

const setDefaultHeadersFromStorage = () => {
  const companyId = getStoredCompanyId();
  const accessToken = getStoredAccessToken();

  if (companyId) {
    axiosInstance.defaults.headers.common["company"] = companyId;
  }

  if (accessToken) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  }
};

const setAccessTokenInStorage = (token: string | null) => {
  if (token) {
    localStorage.setItem("access-token", token);
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem("access-token");
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

const setRefreshTokenInStorage = (token: string | null) => {
  if (token) {
    localStorage.setItem("refresh-token", token);
  } else {
    localStorage.removeItem("refresh-token");
  }
};

const clearAuthStorage = () => {
  localStorage.removeItem("access-token");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("token");
  localStorage.removeItem("refresh-token");
  localStorage.removeItem("company");
  delete axiosInstance.defaults.headers.common["Authorization"];
};

const decodeJwtPayload = (token: string) => {
  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return null;
    }

    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
};

const isJwtTokenValid = (token: string | null) => {
  if (!token) {
    return false;
  }

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") {
    return false;
  }

  return payload.exp * 1000 > Date.now();
};

const refreshAccessToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken || !isJwtTokenValid(refreshToken)) {
    throw new Error("Refresh token expired or missing");
  }

  const refreshInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  const response = await refreshInstance.post("auth/refresh-tokens", {
    refreshToken,
  });

  const newAccessToken = response.data?.tokens?.access?.token;
  const newRefreshToken = response.data?.tokens?.refresh?.token;

  if (newAccessToken) {
    setAccessTokenInStorage(newAccessToken);
  }

  if (newRefreshToken) {
    setRefreshTokenInStorage(newRefreshToken);
  }

  return response;
};

setDefaultHeadersFromStorage();

// let cancelTokenSource: CancelTokenSource;

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const companyId = getStoredCompanyId();
    const accessToken = getStoredAccessToken();

    if (!config.headers) {
      // eslint-disable-next-line no-param-reassign
      config.headers = {} as any;
    }

    if (companyId) {
      // eslint-disable-next-line no-param-reassign
      (config.headers as any)["company"] = companyId;
    }

    if (accessToken) {
      // eslint-disable-next-line no-param-reassign
      (config.headers as any)["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: AxiosError) => {
    // Handle request error
    return Promise.reject(error);
  },
);

// Add response interceptor
// Add response interceptor
axiosInstance.interceptors.response.use(
  (response: any) => {
    return Promise.resolve(response);
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalConfig = (error.config || {}) as any;
    const requestUrl = originalConfig?.url || "";
    const isLoginRequest = requestUrl.includes("auth/login");
    const isRefreshRequest = requestUrl.includes("auth/refresh-tokens");

    if (status === 401 && !isLoginRequest && !isRefreshRequest && !originalConfig._retry) {
      originalConfig._retry = true;

      try {
        await refreshAccessToken();
        const accessToken = getStoredAccessToken();
        if (accessToken) {
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers["Authorization"] = `Bearer ${accessToken}`;
        }
        return axiosInstance(originalConfig);
      } catch (refreshError) {
        clearAuthStorage();
        if (window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
        return Promise.reject(refreshError);
      }
    }

    handleResponse(error.response);
    return Promise.reject(error);
  },
);
export default axiosInstance;
